<template>
  <div class="modal-backdrop">
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="modal-title">Select a Course</h3>
        <button class="close-btn" @click="$emit('close')">&times;</button>
      </div>
      <div class="modal-body">
        <div class="search-container">
          <i class="fas fa-search search-icon"></i>
          <input 
            type="text" 
            :value="searchQuery" 
            @input="$emit('search', $event.target.value)" 
            placeholder="Search courses..." 
            class="search-input"
          />
        </div>

        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p class="loading-text">Loading courses...</p>
        </div>

        <div v-else-if="courses && courses.length > 0" class="courses-list">
          <div 
            v-for="course in courses" 
            :key="course.id"
            class="course-item"
            @click="$emit('select', course)"
          >
            <div class="course-details">
              <div class="course-id">#{{ course.id }}</div>
              <div class="course-title">{{ course.title }}</div>
              <div class="course-instructor" v-if="course.instructor">
                Instructor: {{ course.instructor.first_name }} {{ course.instructor.last_name }}
              </div>
            </div>
            <button class="select-btn">
              <i class="fas fa-check"></i> Select
            </button>
          </div>
        </div>

        <div v-else class="empty-state">
          <i class="fas fa-search empty-icon"></i>
          <p class="empty-text">No courses found</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CoursesSelectionModal',
  props: {
    courses: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchQuery: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'select', 'search']
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 35rem;
  max-height: 90vh;
  animation: slideIn 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #343a40;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.close-btn:hover {
  opacity: 1;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.search-container {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 0.75rem 0.875rem 0.75rem 2.5rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #3498db;
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
}

.spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid rgba(52, 152, 219, 0.2);
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.loading-text {
  color: #6c757d;
  margin: 0;
}

.courses-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
}

.course-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.course-item:last-child {
  border-bottom: none;
}

.course-item:hover {
  background-color: #f8f9fa;
}

.course-details {
  flex: 1;
  min-width: 0;
}

.course-id {
  font-weight: 600;
  color: #3498db;
  margin-bottom: 0.25rem;
}

.course-title {
  font-size: 1rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.course-instructor {
  font-size: 0.75rem;
  color: #6c757d;
}

.select-btn {
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.select-btn:hover {
  background-color: #2980b9;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  color: #6c757d;
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-text {
  margin: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .modal-body {
    padding: 1rem;
  }
  
  .course-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .select-btn {
    align-self: stretch;
    justify-content: center;
  }
}
</style> 